import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronLeft, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useDaySessions } from "../api/hooks/useDaySessions";
import type { TradeSortKey, TradeSortOrder } from "../api/hooks/useTrades";
import { useTrades } from "../api/hooks/useTrades";
import { RunningPnlPanel } from "../components/charts/RunningPnlPanel";
import { FilterBar } from "../components/filters/FilterBar";
import { useAddTradeDrawer } from "../components/trades/AddTradeDrawer";
import { BulkActionToolbar } from "../components/trades/BulkActionToolbar";
import { DaySessionsTable } from "../components/trades/DaySessionsTable";
import { TradePreviewProvider, useTradePreview } from "../components/trades/TradePreviewPanel";
import { SavedFiltersTabStrip } from "../components/trades/SavedFiltersTabStrip";
import { ColumnsButton } from "../components/trades/ColumnsButton";
import { SearchPopover } from "../components/trades/SearchPopover";
import { TradesKpiStrip, TradesKpiStripSkeleton } from "../components/trades/TradesKpiStrip";
import { maxAbsPnl } from "../components/trades/tradeCells";
import {
  FixedTradeCells,
  FixedTradeHeadCells,
  OPTIONAL_COLUMN_LABELS,
  OPTIONAL_COLUMN_ORDER,
  renderOptionalCell,
  type OptionalColumn,
} from "../components/trades/tradeColumns";
import { Button } from "../components/ui/button";
import { TableSkeleton } from "../components/ui/skeleton";
import { buildIntradayCumulative, intradayCurrency } from "../lib/intradayPnl";
import { usePageFilters } from "../lib/pageFilters";
import { readSettings } from "../lib/settings";
import {
  consumeTradesRestore,
  readTradesSnapshot,
  writeTradesSnapshot,
} from "../lib/tradesListState";
import { readTradesViewMode, writeTradesViewMode } from "../lib/tradesViewMode";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const DEFAULT_LIMIT = 50;
const DEFAULT_DAY_LIMIT = 20;
// Flat ("Refined") grid page sizes offered in the pagination footer.
const PAGE_SIZE_OPTIONS = [25, 50, 75, 100] as const;

type TradesSearch = {
  limit?: number;
  offset?: number;
  screen_id?: string;
  sort_key?: TradeSortKey;
  sort_order?: TradeSortOrder;
  since?: string;
  until?: string;
  q?: string;
  from?: "calendar" | "dashboard";
  cal_y?: number;
  cal_m?: number;
};

export function TradesPage() {
  return (
    <TradePreviewProvider>
      <TradesPageContent />
    </TradePreviewProvider>
  );
}

function TradesPageContent() {
  const navigate = useNavigate();
  const { open: openAddTrade } = useAddTradeDrawer();
  const { open: openTradePreview } = useTradePreview();
  const { filters, setFilters } = usePageFilters("trades");
  const search = useSearch({ strict: false }) as TradesSearch;
  const limit = search.limit ?? DEFAULT_LIMIT;
  const offset = search.offset ?? 0;
  const screenId = search.screen_id;
  const sortKey: TradeSortKey = search.sort_key ?? "opened_at";
  const sortOrder: TradeSortOrder = search.sort_order ?? "desc";
  const since = filters.since;
  const until = filters.until;

  const fromCalendar = search.from === "calendar";
  const fromDashboard = search.from === "dashboard";
  const calYear = search.cal_y;
  const calMonth = search.cal_m;
  const openedAfter = since !== undefined ? `${since}T00:00:00.000Z` : undefined;
  const openedBefore = until !== undefined ? `${until}T23:59:59.999Z` : undefined;

  // Symbol search is server-side and lives in the URL `q` param so it survives
  // navigation and covers every trade (not just the loaded page). An active
  // query collapses the grouped view to the flat result list (effectiveGrouped
  // below) — "grouped + filtered to one symbol" is narrow, and the flat query
  // is the single code path that fully answers the search.
  const committedQuery = (search.q ?? "").trim();
  const searchActive = committedQuery !== "";

  // Scalper roll-up thresholds (ADR 0022) — read once, reused for the session
  // view's within-day run coalescing.
  const rollupPrefs = useMemo(() => readSettings().display, []);

  // "Grouped by session" (by trading day) is the default; Ungroup flips to the
  // flat "Refined" grid. The user's explicit choice persists across navigation
  // (localStorage). A single-day drill-in forces flat for the visit via
  // `effectiveGrouped` below (without touching the saved preference); multi-day
  // ranges keep the saved choice and stay groupable.
  const [grouped, setGrouped] = useState<boolean>(() => {
    const stored = readTradesViewMode();
    return stored !== null ? stored === "grouped" : true;
  });
  const toggleGrouped = useCallback(() => {
    setGrouped((prev) => {
      const next = !prev;
      writeTradesViewMode(next ? "grouped" : "flat");
      return next;
    });
  }, []);

  // A single-day drill-in (since === until) collapses every session into one
  // header over the whole day — pure overhead — so force the flat grid for that
  // visit. It's a derived, per-visit override: it never mutates the saved
  // preference, and multi-day ranges stay groupable.
  const singleDayDrillIn = since !== undefined && until !== undefined && since === until;
  // An active symbol search forces the flat result list regardless of the saved
  // grouping preference (which is left untouched, like the single-day override).
  const effectiveGrouped = grouped && !singleDayDrillIn && !searchActive;

  // Round-trip restore (item: "Back to trades"): when arriving via the trade
  // detail's back affordance, re-seed the grouped view's pagination and the set
  // of expanded session days from the snapshot saved when the user left. A fresh
  // visit (no restore flag) starts collapsed at the top. Read once on mount.
  const [restoreState] = useState(() => {
    const restore = consumeTradesRestore();
    const snapshot = restore ? readTradesSnapshot() : null;
    return snapshot;
  });

  // Group pagination is by whole session day (local, reset on filter change).
  const [dayOffset, setDayOffset] = useState(() => restoreState?.dayOffset ?? 0);

  // Which session days are expanded in the grouped view. Lifted out of
  // DaySessionsTable so the open set persists across a trade-detail round-trip.
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(
    () => new Set(restoreState?.expandedSessions ?? []),
  );
  const toggleSession = useCallback((key: string) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Continuously snapshot the list's restorable state (URL search + grouped
  // pagination + expanded days) so the trade-detail back link can return here
  // exactly as the user left it. Filters (since/until) ride the URL/page-filter
  // memory; scroll is handled by the router's pathname-keyed restoration.
  useEffect(() => {
    writeTradesSnapshot({
      search,
      dayOffset,
      expandedSessions: Array.from(expandedSessions),
    });
  }, [search, dayOffset, expandedSessions]);

  const sessionsQuery = useDaySessions({
    closed_after: openedAfter,
    closed_before: openedBefore,
    day_limit: DEFAULT_DAY_LIMIT,
    day_offset: dayOffset,
    window_minutes: rollupPrefs.scalperWindowMinutes,
    min_run: rollupPrefs.scalperMinRun,
  });

  const tradesQuery = useTrades(
    {
      limit,
      offset,
      sort_key: sortKey,
      sort_order: sortOrder,
      ...(screenId !== undefined ? { screen_id: screenId } : {}),
      ...(openedAfter !== undefined ? { opened_after: openedAfter } : {}),
      ...(openedBefore !== undefined ? { opened_before: openedBefore } : {}),
      ...(searchActive ? { symbol_query: committedQuery } : {}),
    },
    !effectiveGrouped,
  );

  const [visibility, setVisibility] = useState<Record<OptionalColumn, boolean>>({
    status: false,
    leg_count: false,
    planned_risk: false,
    hold_duration: false,
    r_multiple: false,
    strategy: false,
    notes: false,
  });

  const items = useMemo(() => tradesQuery.data?.items ?? [], [tradesQuery.data]);

  // Local mirror of the search box for responsive typing; debounced into the
  // URL `q` param below. Server-side filtering means no client-side row pruning
  // here — the flat query already returns only matching trades.
  const [queryInput, setQueryInput] = useState(search.q ?? "");
  const filteredItems = items;
  const filteredSessions = sessionsQuery.data?.sessions ?? [];

  const visibleOptionalColumns = OPTIONAL_COLUMN_ORDER.filter((key) => visibility[key]);

  const flatGaugeMax = useMemo(
    () => maxAbsPnl(filteredItems.map((trade) => trade.realized_pnl_amount)),
    [filteredItems],
  );

  const [selectedTradeIds, setSelectedTradeIds] = useState<Set<string>>(new Set());

  const visibleIds = useMemo(() => filteredItems.map((t) => t.trade_id), [filteredItems]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedTradeIds.has(id));
  const someVisibleSelected =
    !allVisibleSelected && visibleIds.some((id) => selectedTradeIds.has(id));

  const toggleRow = useCallback((tradeId: string) => {
    setSelectedTradeIds((prev) => {
      const next = new Set(prev);
      if (next.has(tradeId)) {
        next.delete(tradeId);
      } else {
        next.add(tradeId);
      }
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedTradeIds((prev) => {
      const next = new Set(prev);
      const allOn = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
      if (allOn) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => setSelectedTradeIds(new Set()), []);

  const setRowsSelected = useCallback((ids: readonly string[], selected: boolean) => {
    setSelectedTradeIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
      return next;
    });
  }, []);

  // The grouped view's selection universe is every constituent trade across the
  // visible sessions.
  const groupedConstituents = useMemo(
    () => filteredSessions.flatMap((session) => session.groups.flatMap((g) => g.trades)),
    [filteredSessions],
  );
  const groupedSelectedDeletedCount = useMemo(
    () =>
      groupedConstituents.filter((t) => selectedTradeIds.has(t.trade_id) && t.is_deleted).length,
    [groupedConstituents, selectedTradeIds],
  );

  const originSearch = useMemo(() => {
    const ym = {
      ...(calYear !== undefined ? { cal_y: calYear } : {}),
      ...(calMonth !== undefined ? { cal_m: calMonth } : {}),
    };
    if (fromCalendar) return { from: "calendar" as const, ...ym };
    if (fromDashboard) return { from: "dashboard" as const, ...ym };
    return {};
  }, [fromCalendar, fromDashboard, calYear, calMonth]);

  const toggleDateSort = useCallback(() => {
    const nextOrder: TradeSortOrder = sortOrder === "desc" ? "asc" : "desc";
    void navigate({
      to: "/trades",
      search: {
        limit,
        offset: 0,
        sort_key: "opened_at",
        sort_order: nextOrder,
        ...(screenId !== undefined ? { screen_id: screenId } : {}),
        ...(since !== undefined ? { since } : {}),
        ...(until !== undefined ? { until } : {}),
        ...originSearch,
      },
    });
  }, [navigate, limit, sortOrder, screenId, since, until, originSearch]);

  const goToOffset = useCallback(
    (nextOffset: number) => {
      void navigate({
        to: "/trades",
        search: {
          limit,
          offset: nextOffset,
          sort_key: sortKey,
          sort_order: sortOrder,
          ...(screenId !== undefined ? { screen_id: screenId } : {}),
          ...(since !== undefined ? { since } : {}),
          ...(until !== undefined ? { until } : {}),
          ...originSearch,
        },
      });
    },
    [navigate, limit, sortKey, sortOrder, screenId, since, until, originSearch],
  );

  const setPageSize = useCallback(
    (nextLimit: number) => {
      void navigate({
        to: "/trades",
        // Reset to the first page: the prior offset may exceed the row count
        // at a larger page size, which would land on an empty page.
        search: {
          limit: nextLimit,
          offset: 0,
          sort_key: sortKey,
          sort_order: sortOrder,
          ...(screenId !== undefined ? { screen_id: screenId } : {}),
          ...(since !== undefined ? { since } : {}),
          ...(until !== undefined ? { until } : {}),
          ...originSearch,
        },
      });
    },
    [navigate, sortKey, sortOrder, screenId, since, until, originSearch],
  );

  // Debounce the search box into the URL `q` param (which drives the server
  // query) and reset to the first page on a new term. Only navigates when the
  // trimmed value actually changes, so mount and unrelated re-renders are no-ops.
  useEffect(() => {
    const trimmed = queryInput.trim();
    if (trimmed === committedQuery) return;
    const handle = setTimeout(() => {
      void navigate({
        to: "/trades",
        search: {
          ...(trimmed !== "" ? { q: trimmed } : {}),
          limit,
          offset: 0,
          sort_key: sortKey,
          sort_order: sortOrder,
          ...(screenId !== undefined ? { screen_id: screenId } : {}),
          ...(since !== undefined ? { since } : {}),
          ...(until !== undefined ? { until } : {}),
          ...originSearch,
        },
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [
    queryInput,
    committedQuery,
    navigate,
    limit,
    sortKey,
    sortOrder,
    screenId,
    since,
    until,
    originSearch,
  ]);

  const selectedDeletedCount = useMemo(
    () => items.filter((t) => selectedTradeIds.has(t.trade_id) && t.is_deleted).length,
    [items, selectedTradeIds],
  );

  const selectedTradeIdList = useMemo(
    () => Array.from(selectedTradeIds),
    [selectedTradeIds],
  );

  const sortCaret = sortKey === "opened_at" ? (sortOrder === "desc" ? "▼" : "▲") : "";
  const totals = sessionsQuery.data?.totals;
  const monthLabel = useMemo(() => {
    const session = sessionsQuery.data?.sessions?.find((s) => s.session_date !== null);
    if (session?.session_date == null) return "";
    return new Date(`${session.session_date}T00:00:00Z`).toLocaleDateString(undefined, {
      timeZone: "UTC",
      month: "long",
      year: "numeric",
    });
  }, [sessionsQuery.data]);

  // Intraday running-P&L chart: only when drilling into a single day from a
  // day click on either calendar — the full Calendar page or the dashboard's
  // calendar widget (both land here with from=calendar|dashboard and a single
  // since===until day). The day-sessions query is already loaded for the same
  // close-date window, so the series is built from its rows — no extra fetch —
  // and its final total reconciles with the clicked day cell.
  const showIntradayChart = (fromCalendar || fromDashboard) && singleDayDrillIn;
  const intradayPoints = useMemo(
    () => (showIntradayChart ? buildIntradayCumulative(sessionsQuery.data?.sessions ?? []) : []),
    [showIntradayChart, sessionsQuery.data],
  );
  const intradayCcy = useMemo(
    () => intradayCurrency(sessionsQuery.data?.sessions ?? []),
    [sessionsQuery.data],
  );
  const intradayDayLabel = useMemo(() => {
    if (since === undefined) return "";
    const [y, m, d] = since.split("-").map(Number);
    if (!y || !m || !d) return "";
    const date = new Date(Date.UTC(y, m - 1, d));
    const weekday = date.toLocaleDateString(undefined, { timeZone: "UTC", weekday: "short" });
    const monthDay = date.toLocaleDateString(undefined, {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    });
    return `${weekday}, ${monthDay} ${y}`;
  }, [since]);

  return (
    <section className="p-6 space-y-4">
      {fromCalendar ? (
        <Link
          to="/calendar"
          search={{
            ...(calYear !== undefined ? { y: calYear } : {}),
            ...(calMonth !== undefined ? { m: calMonth } : {}),
            view: "month",
          }}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          data-testid="back-to-calendar"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Back to calendar
        </Link>
      ) : fromDashboard ? (
        <Link
          to="/dashboard"
          search={{
            ...(calYear !== undefined ? { cal_y: calYear } : {}),
            ...(calMonth !== undefined ? { cal_m: calMonth } : {}),
          }}
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          data-testid="back-to-dashboard"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Back to dashboard
        </Link>
      ) : null}
      <header className="flex items-baseline gap-3">
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">Trades</h1>
        <span className="text-sm text-on-surface-variant">
          {effectiveGrouped ? "Grouped by session" : "All trades"}
          {monthLabel !== "" ? ` · ${monthLabel}` : ""}
        </span>
      </header>

      {/* Toolbar: date filters, filter tabs, search, columns, ungroup, add
          trade — all above the KPI strip (WickScope Grouped layout). */}
      <div className="flex flex-wrap items-center gap-3">
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          showDateRange
          aria-label="Trades filters"
        />
        <div className="min-w-0 flex-1">
          <SavedFiltersTabStrip activeScreenId={screenId} />
        </div>
        <SearchPopover value={queryInput} onChange={setQueryInput} />
        {effectiveGrouped ? null : (
          <ColumnsButton
            columns={OPTIONAL_COLUMN_ORDER.map((key) => ({
              key,
              label: OPTIONAL_COLUMN_LABELS[key],
            }))}
            visibility={visibility}
            onToggle={(key, checked) =>
              setVisibility((prev) => ({ ...prev, [key]: checked }))
            }
          />
        )}
        {singleDayDrillIn ? null : (
          <Button
            type="button"
            variant="outline"
            aria-pressed={grouped}
            onClick={toggleGrouped}
          >
            {grouped ? "Ungroup" : "Group by session"}
          </Button>
        )}
        <Button
          type="button"
          onClick={() => {
            openAddTrade();
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add trade
        </Button>
      </div>

      {totals !== undefined ? (
        <TradesKpiStrip totals={totals} />
      ) : sessionsQuery.isLoading ? (
        <TradesKpiStripSkeleton />
      ) : null}

      {showIntradayChart ? (
        <RunningPnlPanel
          points={intradayPoints}
          currency={intradayCcy}
          dayLabel={intradayDayLabel}
          loading={sessionsQuery.isLoading}
        />
      ) : null}

      {effectiveGrouped ? renderGroupedView() : renderUngroupedView()}
    </section>
  );

  function renderGroupedView() {
    return (
      <>
        <BulkActionToolbar
          selectedTradeIds={selectedTradeIdList}
          selectedDeletedCount={groupedSelectedDeletedCount}
          onClear={clearSelection}
        />
        {sessionsQuery.isLoading ? (
          <div role="status" aria-live="polite">
            <span className="sr-only">Loading trades</span>
            <TableSkeleton rows={8} />
          </div>
        ) : sessionsQuery.isError ? (
          <p className="text-danger" role="alert">
            {sessionsQuery.error instanceof Error
              ? sessionsQuery.error.message
              : "Failed to load trades."}
          </p>
        ) : filteredSessions.length === 0 ? (
          <p className="text-on-surface-variant">
            {searchActive ? "No trades match your search." : "No trades found."}
          </p>
        ) : (
          <>
            <DaySessionsTable
              sessions={filteredSessions}
              selectedTradeIds={selectedTradeIds}
              onSetRowsSelected={setRowsSelected}
              expandedSessions={expandedSessions}
              onToggleSession={toggleSession}
            />
            <nav className="flex items-center justify-between" aria-label="Session pagination">
              <button
                type="button"
                className="rounded border border-outline px-3 py-1 text-sm text-on-surface hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={dayOffset === 0}
                onClick={() => setDayOffset((prev) => Math.max(0, prev - DEFAULT_DAY_LIMIT))}
              >
                Previous
              </button>
              <span className="text-sm text-on-surface-variant">
                {sessionsQuery.data?.has_more === true ? "More sessions" : "End of list"}
              </span>
              <button
                type="button"
                className="rounded border border-outline px-3 py-1 text-sm text-on-surface hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={sessionsQuery.data?.has_more !== true}
                onClick={() => setDayOffset((prev) => prev + DEFAULT_DAY_LIMIT)}
              >
                Next
              </button>
            </nav>
          </>
        )}
      </>
    );
  }

  function renderUngroupedView() {
    return (
      <>
        <BulkActionToolbar
          selectedTradeIds={selectedTradeIdList}
          selectedDeletedCount={selectedDeletedCount}
          onClear={clearSelection}
        />

        <main className="space-y-4 min-w-0">
          {tradesQuery.isLoading ? (
            <div role="status" aria-live="polite">
              <span className="sr-only">Loading trades</span>
              <TableSkeleton rows={8} />
            </div>
          ) : tradesQuery.isError ? (
            <p className="text-danger" role="alert">
              {tradesQuery.error instanceof Error ? tradesQuery.error.message : "Failed to load trades."}
            </p>
          ) : tradesQuery.data === undefined || filteredItems.length === 0 ? (
            <p className="text-on-surface-variant">
              {searchActive ? "No trades match your search." : "No trades found."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all visible"
                        checked={allVisibleSelected}
                        ref={(el) => {
                          if (el !== null) {
                            el.indeterminate = someVisibleSelected;
                          }
                        }}
                        onChange={toggleAllVisible}
                        className="h-4 w-4"
                      />
                    </TableHead>
                    <TableHead aria-sort={sortOrder === "desc" ? "descending" : "ascending"}>
                      <button
                        type="button"
                        onClick={toggleDateSort}
                        className="inline-flex items-center gap-1"
                      >
                        Date / Time
                        <span aria-hidden="true">{sortCaret}</span>
                      </button>
                    </TableHead>
                    <FixedTradeHeadCells omitFirst />
                    {visibleOptionalColumns.map((key) => (
                      <TableHead key={key}>{OPTIONAL_COLUMN_LABELS[key]}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((trade) => (
                    <TableRow
                      key={trade.trade_id}
                      onClick={() => openTradePreview(trade.trade_id)}
                      className="cursor-pointer hover:bg-surface-container-low"
                    >
                      <TableCell className="w-8">
                        <input
                          type="checkbox"
                          aria-label={`Select trade ${trade.trade_id}`}
                          checked={selectedTradeIds.has(trade.trade_id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => toggleRow(trade.trade_id)}
                          className="h-4 w-4"
                        />
                      </TableCell>
                      <FixedTradeCells trade={trade} gaugeMax={flatGaugeMax} interactiveRow />
                      {visibleOptionalColumns.map((key) => (
                        <TableCell key={key}>{renderOptionalCell(key, trade)}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <nav className="flex items-center justify-between gap-4" aria-label="Pagination">
            <label className="flex items-center gap-2 text-sm text-on-surface-variant">
              Rows per page
              <select
                className="rounded border border-outline bg-surface px-2 py-1 text-sm text-on-surface"
                value={limit}
                onChange={(event) => setPageSize(Number(event.target.value))}
                aria-label="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="rounded border border-outline px-3 py-1 text-sm text-on-surface hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={offset === 0}
                onClick={() => goToOffset(Math.max(0, offset - limit))}
              >
                Previous
              </button>
              <span className="text-sm text-on-surface-variant">
                {tradesQuery.data?.has_more === true ? "More available" : "End of list"}
              </span>
              <button
                type="button"
                className="rounded border border-outline px-3 py-1 text-sm text-on-surface hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={tradesQuery.data?.has_more !== true}
                onClick={() => goToOffset(offset + limit)}
              >
                Next
              </button>
            </div>
          </nav>
        </main>
      </>
    );
  }
}
